import { Check, Minus, X } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { ApplicationData } from '@/types/app';
import SectionCard from './SectionCard';

interface EndQuestionsProps {
	applicationData: ApplicationData;
}

/** The final declaration questions, in the order the customer answers them. */
const END_QUESTIONS: ReadonlyArray<{ name: keyof ApplicationData; labelKey: string }> = [
	{ name: 'agreeToTerms', labelKey: 'application.steps.step5.agreeToTerms' },
	{ name: 'agreeToCancel', labelKey: 'application.steps.step5.agreeToCancel' },
	{ name: 'hasNoLegalIssues', labelKey: 'application.steps.step6.hasNoLegalIssues' },
	{ name: 'hasNoInsuranceIssues', labelKey: 'application.steps.step6.hasNoInsuranceIssues' },
	{ name: 'hasNoBankruptcyIssues', labelKey: 'application.steps.step6.hasNoBankruptcyIssues' }
];

/**
 * Read-only view of the customer's answers to the final declaration questions.
 * A boolean maps to yes/no; a missing answer shows "not answered".
 */
const EndQuestions = ({ applicationData }: EndQuestionsProps): React.JSX.Element => {
	const { t } = useTranslation();

	return (
		<SectionCard
			title={t('requestOverview.endQuestions.title')}
			testId="dossier-section-end-questions"
			bodyClassName="p-6 sm:p-7"
		>
			<ul className="flex flex-col gap-3">
				{END_QUESTIONS.map(({ name, labelKey }) => {
					const answer = applicationData[name];
					const answered = typeof answer === 'boolean';
					const isYes = answer === true;

					return (
						<li
							key={name}
							className="flex items-start gap-4 border-b border-border/60 pb-4 last:border-b-0 last:pb-0"
							data-testid={`dossier-end-question-${name}`}
						>
							<span
								className={cn(
									'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
									!answered
										? 'bg-muted text-muted-foreground'
										: isYes
											? 'bg-green-500 text-white'
											: 'bg-destructive text-destructive-foreground'
								)}
							>
								{!answered ? (
									<Minus className="h-3 w-3" aria-hidden />
								) : isYes ? (
									<Check className="h-3 w-3" aria-hidden />
								) : (
									<X className="h-3 w-3" aria-hidden />
								)}
							</span>
							<div className="min-w-0">
								<p className="text-sm leading-relaxed text-foreground">{t(labelKey)}</p>
								<p
									className={cn(
										'mt-2 text-xs font-semibold',
										!answered
											? 'italic text-muted-foreground/70'
											: isYes
												? 'text-green-700'
												: 'text-destructive'
									)}
								>
									{!answered
										? t('requestOverview.endQuestions.notAnswered')
										: isYes
											? t('requestOverview.yes')
											: t('requestOverview.no')}
								</p>
							</div>
						</li>
					);
				})}
			</ul>
		</SectionCard>
	);
};

export default EndQuestions;
